import { useCallback, useState } from "react";

import { Id } from "@cvx/_generated/dataModel";
import { api } from "@cvx/_generated/api";
import { useAction } from "convex/react";

export interface PaymentMethod {
  id: string;
  brand?: string;
  last4?: string;
  expMonth?: number;
  expYear?: number;
}

export interface UsePaymentMethodsReturn {
  paymentMethods: PaymentMethod[];
  hasPaymentMethod: boolean;
  isLoading: boolean;
  isChecked: boolean;
  checkPaymentMethods: (
    userId: Id<"users">,
  ) => Promise<{ hasPaymentMethod: boolean; paymentMethods: PaymentMethod[] }>;
  formatCardBrand: (brand?: string) => string;
  reset: () => void;
}

export function usePaymentMethods(): UsePaymentMethodsReturn {
  const getCustomerPaymentMethods = useAction(
    api.stripe.getCustomerPaymentMethods,
  );

  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [hasPaymentMethod, setHasPaymentMethod] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isChecked, setIsChecked] = useState<boolean>(false);

  const checkPaymentMethods = useCallback(
    async (userId: Id<"users">) => {
      // If already checked, return cached results
      if (isChecked) {
        return { hasPaymentMethod, paymentMethods };
      }

      setIsLoading(true);
      try {
        const result = await getCustomerPaymentMethods({ userId });
        setHasPaymentMethod(result.hasPaymentMethod);
        setPaymentMethods(result.paymentMethods);
        setIsChecked(true);

        return {
          hasPaymentMethod: result.hasPaymentMethod,
          paymentMethods: result.paymentMethods,
        };
      } catch (error) {
        console.error("Error checking payment methods:", error);
        setHasPaymentMethod(false);
        setPaymentMethods([]);
        setIsChecked(true);

        return {
          hasPaymentMethod: false,
          paymentMethods: [],
        };
      } finally {
        setIsLoading(false);
      }
    },
    [getCustomerPaymentMethods, isChecked, hasPaymentMethod, paymentMethods],
  );

  const formatCardBrand = useCallback((brand?: string) => {
    if (!brand) return "Card";
    return brand.charAt(0).toUpperCase() + brand.slice(1);
  }, []);

  const reset = useCallback(() => {
    setPaymentMethods([]);
    setHasPaymentMethod(false);
    setIsLoading(false);
    setIsChecked(false);
  }, []);

  return {
    paymentMethods,
    hasPaymentMethod,
    isLoading,
    isChecked,
    checkPaymentMethods,
    formatCardBrand,
    reset,
  };
}
